import React, {useMemo, useEffect, useState} from 'react';
import {
  useTable,
  usePagination,
  useSortBy,
  useFilters,
  useGlobalFilter,
  useExpanded,
} from 'react-table';
import cs from './styles';
import {getSubCellClass} from './tableUtils';
import moment from 'moment';
import _isUndefined from 'lodash/isUndefined';
import _isNull from 'lodash/isNull';
import Paginate from '../Paginate';
import {DropdownFilter, DateRangeFilter, GlobalFilter} from './DataFilter';
import Button from './ActionButton';
import classnames from 'classnames';
import store from 'store';
import _isEmpty from 'lodash/isEmpty';
import _pullAt from 'lodash/pullAt';
import _cloneDeep from 'lodash/cloneDeep';
import './dataTable.css';
import {Trans} from 'react-i18next';

export let forceFetch = null;

export default function DataTable({
  data = [],
  columns,
  loading = false,
  initState = {},
  noDataMessage,
  enableGlobalSearch = false,
  pageChangedCallback,
  onFetchData = null,
  storeParams = [],
  setPageData,
  headerStyle,
  isPage = true,
  isModalSetting = false,
  customRenderRowSubComponent,
  tableClassName = 'table-striped table-bordered table-hover',
  customTableStyle,
  rowProps = row => ({}),
}) {
  const [manualData, setManualData] = useState([]);
  const [manualTotalSize, setManualTotalSize] = useState(0);
  const [temp, setTemp] = useState(0);
  const filterable = columns.findIndex((c) => c.Filter) !== -1;
  const processedColumns = columns.map((c) => ({
    ...c,
    filter: getFilterType(c),
  }));
  forceFetch = () => setTemp(temp + 1);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    rows,
    gotoPage,
    setPageSize,
    setAllFilters,
    visibleColumns,
    state: {pageIndex, pageSize, expanded, globalFilter},
    setGlobalFilter,
  } = useTable(
    {
      columns: useMemo(() => processedColumns, [columns]),
      data: onFetchData ? manualData : data,
      filterTypes: useMemo(
        () => ({
          dateRange: (rows, id, filterValue) => {
            if (filterValue[0] === '' && filterValue[1] === '') return rows;
            return rows.filter((row) => {
              const value = row.values[id];
              return _isUndefined(value) || value === null
                ? false
                : moment(row.values[id]).isBetween(
                    filterValue[0],
                    filterValue[1],
                    null,
                    '[]'
                  );
            });
          },
          dropdown: (rows, id, filterValue) => {
            if (!filterValue) return rows;
            return rows.filter((row) => {
              const columnValue = row.values[id];
              if (!columnValue) return false;
              if (Array.isArray(columnValue)) {
                return columnValue.includes(filterValue) ? columnValue : false;
              }
              if (typeof columnValue === 'object') {
                return columnValue.value === filterValue;
              }
              return columnValue === filterValue;
            });
          },
          text: (rows, id, filterValue) => {
            if (!filterValue) return rows;
            return rows.filter((row) => {
              const cellValue = row.values[id];
              if (_isNull(cellValue) || _isUndefined(cellValue)) return false;
              if (typeof cellValue === 'object') {
                return Object.keys(cellValue).some(
                  (k) =>
                    String(cellValue[k])
                      .toLowerCase()
                      .indexOf(String(filterValue).toLowerCase()) !== -1
                );
              }
              return (
                String(cellValue)
                  .toLowerCase()
                  .indexOf(String(filterValue).toLowerCase()) !== -1
              );
            });
          },
        }),
        []
      ),
      autoResetPage: false,
      disableSortRemove: true,
      disableMultiSort: true,
      initialState: {
        ...initState,
        pageSize: initState.pageSize || 10,
        sortBy: initState.sortBy
          ? !Array.isArray(initState.sortBy)
            ? [initState.sortBy]
            : initState.sortBy
          : [],
      },
      manualPagination: !!onFetchData,
      pageCount: onFetchData ? manualTotalSize : data.length,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination
  );

  useEffect(() => {
    if (onFetchData) {
      onFetchData({pageIndex: pageIndex + 1, pageSize}).then(
        ({results, totalSize}) => {
          setManualData(results);
          setManualTotalSize(totalSize);
        }
      );
    }
    if (setPageData) setPageData(page);
  }, [onFetchData, pageIndex, pageSize, temp]);

  useEffect(() => {
    if (!onFetchData && setPageData) {
      setPageData(page);
    }
  }, [data]);

  const SubRows = ({row, rowProps, visibleColumns, data}) => {
    if (!data) return null;
    const allVer = Object.keys(data);
    _pullAt(allVer, 0);

    const newData = allVer.map((k) => ({
      ...row.original,
      makeupInfo: {[k]: data[k]},
    }));

    return (
      <>
        {newData.map((x, i) => {
          return (
            <tr {...rowProps} key={`${rowProps.key}-expanded-${i}`}>
              {row.cells.map((cell) => {
                return (
                  <td
                    {...cell.getCellProps()}
                    style={cs.vcenter}
                    className={getSubCellClass(cell)}
                  >
                    {cell.render(cell.column.SubCell ? 'SubCell' : 'Cell', {
                      value: cell.column.accessor && cell.column.accessor(x, i),
                      row: {...row, original: x},
                      subRowIndex: i,
                    })}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </>
    );
  };

  function SubRowAsync({row, rowProps, visibleColumns}) {
    const data = _cloneDeep(row.values.makeupver);
    return (
      <SubRows
        row={row}
        rowProps={rowProps}
        visibleColumns={visibleColumns}
        data={data}
      />
    );
  }

  const renderRowSubComponent = React.useCallback(
    ({row, visibleColumns}) => (
      <SubRowAsync
        row={row}
        rowProps={row.getRowProps()}
        visibleColumns={visibleColumns}
      />
    ),
    []
  );

  return (
    <>
      {enableGlobalSearch ? (
        <div>
          {/*<div style={{flex: '0 0 300px'}} />*/}
          <div style={{textAlign: 'end'}}>
            <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
          </div>
        </div>
      ) : (
        ''
      )}
      <table
        {...getTableProps()}
        className={'table dataTable' + ' ' + tableClassName}
        style={
          customTableStyle
            ? customTableStyle
            : {tableLayout: 'fixed', overflow: 'hidden', overflowY: 'auto'}
        }
      >
        <thead>
          {headerGroups.map((headerGroup, i) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={`head-row-${i}`}>
              {headerGroup.headers.map((column, i) => {
                const {
                  getHeaderProps,
                  getSortByToggleProps,
                  disableSortBy,
                  isSorted,
                  isSortedDesc,
                  render,
                  headerClass,
                  style,
                } = column;
                return column.disableSorting ? (
                  <th
                    className={headerClass || ''}
                    style={{
                      textAlign: 'center',
                      width: style && style.width ? style.width : 'auto',
                      ...headerStyle,
                    }}
                  >
                    {render('Header')}
                  </th>
                ) : (
                  <th
                    key={`head-col-${i}`}
                    {...getHeaderProps(getSortByToggleProps())}
                    style={{
                      textAlign: 'center',
                      width: style && style.width ? style.width : 'auto',
                      ...headerStyle,
                    }}
                    className={classnames(
                      sortingStatus(disableSortBy, isSorted, isSortedDesc),
                      headerClass || ''
                    )}
                  >
                    {render('Header')}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        {filterable && (
          <thead>
            {headerGroups.map((headerGroup, i) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={`filter-${i}`}>
                {headerGroup.headers.map((column, ii) => {
                  const {getHeaderProps, canFilter, id, render, Filter, style} =
                    column;
                  return (
                    <th
                      {...getHeaderProps()}
                      style={style}
                      key={`filter-col-${ii}`}
                    >
                      {canFilter && Filter ? render('Filter') : null}
                      {id === 'actions' && (
                        <Button
                          type="reset"
                          onClick={() => {
                            if (!_isEmpty(storeParams)) {
                              storeParams.forEach((s) => {
                                store.set(s, '');
                              });
                            }
                            setAllFilters([]);
                          }}
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
        )}
        <tbody {...getTableBodyProps()} style={{textAlign: 'center'}}>
          {page.length === 0 && loading && <Loading colSpan={columns.length} />}
          {page.length === 0 && !loading && (
            <NoData colSpan={columns.length} message={noDataMessage} />
          )}
          {page.length > 0 &&
            page.map((row, i) => {
              return (
                prepareRow(row) || (
                  <>
                    <tr
                      {...row.getRowProps(rowProps(row))}
                      key={`tr-${i}`}
                      // onDragStart={() =>
                      //   rows.forEach(r => r.toggleExpanded(false))
                      // }
                    >
                      {row.cells.map((cell, ii) => {
                        return (
                          <td
                            {...cell.getCellProps({
                              className: cell.column.className || '',
                              style: {
                                ...cell.column.style,
                                whiteSpace: 'pre-wrap',
                                overflowWrap: 'break-word',
                                verticalAlign: 'middle',
                              },
                            })}
                            key={`tr${i}-td${ii}`}
                          >
                            {cell.render('Cell')}
                          </td>
                        );
                      })}
                    </tr>
                    {row.isExpanded &&
                      !customRenderRowSubComponent &&
                      renderRowSubComponent({row, visibleColumns})}
                    {row.isExpanded &&
                      customRenderRowSubComponent &&
                      customRenderRowSubComponent({row, visibleColumns})}
                  </>
                )
              );
            })}
        </tbody>
      </table>
      {isPage && (
        <Paginate
          classname={classnames({
            datatable: true,
            paginateModalSetting: isModalSetting,
          })}
          grid={{
            pageIndex: pageIndex + 1,
            pages: Math.ceil(
              (onFetchData ? manualTotalSize : rows.length) / pageSize
            ),
            pageSize,
            totalSize: onFetchData ? manualTotalSize : rows.length,
          }}
          clickCallback={(e) => gotoPage(e.selected)}
          selectCallback={setPageSize}
        />
      )}
    </>
  );
}

function sortingStatus(disable, isSorted, isSortedDesc) {
  if (disable) return '';
  if (!isSorted) return 'sorting';
  return isSortedDesc ? 'sorting_desc' : 'sorting_asc';
}

function NoData({colSpan, message}) {
  return (
    <tr>
      <td colSpan={colSpan} className="text-center">
        {/*<FormattedMessage {...message} />*/}
        <Trans i18nKey="c.table.filters.nodata">No Data</Trans>
      </td>
    </tr>
  );
}

function Loading({colSpan}) {
  return (
    <tr>
      <td colSpan={colSpan}>
        <div>Loading...</div>
      </td>
    </tr>
  );
}

function getFilterType(col) {
  if (col.filter) return col.filter;
  switch (col.Filter) {
    case DropdownFilter:
      return 'dropdown';
    case DateRangeFilter:
      return 'dateRange';
    default:
      return 'text';
  }
}
